import { graphQlClient } from "lib/graphql-client.js";
import dynamic from "next/dynamic";
import React, { useEffect, useState } from "react";

import { fetchDestinationsByRegion } from "lib/api/strapi-destinations";
import { destinationIcons } from "lib/data/icons/destination-icons";
import Head from "next/head";
import Layout from "../../components/Layout";
import { site } from "../../config";

import PageIntro from "components/content/PageIntro/PageIntro";
const ScrollNav = dynamic(
  () => import("components/ui/nav/ScrollNav/ScrollNav")
);

import CardRow from "components/card/CardRow";
const DestinationLinkCard = dynamic(
  () => import("components/card/DestinationLinkCard")
);

import Accordion from "components/ui/Accordion/Accordion";
import AccordionItem from "components/ui/Accordion/AccordionItem";
import { fetchLayoutData, fetchLayoutDataServer } from "lib/api/strapi-layout";

import styles from "./RegionsMain.module.scss";

import useWindowSize from "lib/hooks/useWindowSize";

const RegionsMain = ({ pageData, regions, sliders, layoutData }) => {
  const SEO = {
    ...pageData?.seo,
    canonicalURL: site + "/destinations",
  };

  const [accordionItemProps, setAccordionItemProps] = useState({
    isPageSection: true,

    // classNames: ["scroll-margin-top-180"],
    startOpen: true,
    isOpen: true,
  });

  const windowSize = useWindowSize();

  useEffect(() => {
    if (windowSize.width < 768) {
      setAccordionItemProps((prevState) => {
        return {
          ...prevState,
          startOpen: false,
          isOpen: false,

          titleFontSize: "var(--font-size-md)",
        };
      });
    } else {
      setAccordionItemProps((prevState) => {
        return {
          ...prevState,
          startOpen: true,
          isOpen: true,
          seeAll: true,
          titleFontSize: "var(--font-size-lg)",
        };
      });
    }
  }, [windowSize]);

  const regionTags = regions.map((region) => {
    const navTag = {
      tag: `${region.region}-section`,
      text: `${region.regionName}`,
    };
    return navTag;
  });

  const regionCardRows = regions.map((region, index) => {
    const destinationData =
      region.featuredDestinations?.length > 0
        ? region.featuredDestinations
        : region.destinations;
    const destinationCards = destinationData.map((destination, index) => {
      return (
        <DestinationLinkCard
          destination={destination}
          key={`destinationcard-${index}`}
        />
      );
    });

    return (
      <AccordionItem
        title={region.regionName}
        seeAllLink={`/destinations/${region.region}`}
        icon={destinationIcons[region.region.replace("-", "_")].icon}
        key={`cardrow-${index}`}
        id={`${region.region}-section`}
        {...accordionItemProps}
        classNames={["scroll-margin-top-150"]}
      >
        <CardRow
          cards={destinationCards}
          // sectionTitle={region.regionName}
          sectionSlug={`/destinations/${region.region}`}
          titleLink={`/${region.region}`}
          seeAll={false}
        />
      </AccordionItem>
    );
  });

  return (
    <>
      <Head>
        <link
          rel="dns-prefetch"
          href="https://api.openweathermap.org"
          as="fetch"
        />
        <link rel="dns-prefetch" href="https://api.mapbox.com" as="fetch" />
      </Head>
      <Layout seo={SEO} layoutData={layoutData}>
        <div className={`${styles.scrollNavWrapper} variable-container`}>
          <ScrollNav
            anchorTags={regionTags}
            justify="start"
            style="miniButtons"
          />
        </div>
        <main
          id="mainContent"
          className={`variable-container ${styles.content}`}
        >
          <PageIntro
            title={pageData?.page_intro?.page_title}
            description={pageData?.page_intro?.page_introduction}
            image={pageData?.page_intro?.intro_photo?.url}
            layout="imageRight"
          />

          <Accordion>{regionCardRows}</Accordion>
        </main>
      </Layout>
    </>
  );
};

export default RegionsMain;

export async function getStaticProps() {
  const { gql } = await import("@apollo/client");
  const strapiApolloClient = graphQlClient();

  const { data } = await strapiApolloClient.query({
    query: gql`
      query DestinationPageQuery {
        destinationPage {
          data {
            attributes {
              search_engine_optimization {
                meta {
                  title
                  content
                }

                metaDescription
                metaTitle
                preventIndexing
                metaMedia {
                  data {
                    attributes {
                      url
                      alternativeText
                    }
                  }
                }
              }
              page_intro {
                page_title
                page_introduction
                intro_photo {
                  data {
                    attributes {
                      url
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
  });

  const destinationPage = data.destinationPage.data.attributes;

  const pageData = {
    seo: {
      ...destinationPage.search_engine_optimization,
      metaMedia: {
        ...destinationPage.search_engine_optimization.metaMedia.data.attributes,
      },
    },
    page_intro: {
      ...destinationPage?.page_intro,
      intro_photo: {
        ...destinationPage?.page_intro?.intro_photo?.data?.attributes,
      },
    },
  };

  // const destinations = await fetchDestinationCards({ start: 0, limit: 300 });

  const regions = await fetchDestinationsByRegion({
    regions: [
      "north-america",
      "south-america",
      "europe",
      "asia",
      "africa",
      "oceania",
      "caribbean",
      "central-america",
      "middle-east",
    ],
    featured: true,
  });

  // const layoutData = await fetchLayoutData();
  const layoutData = await fetchLayoutDataServer();
  return {
    props: {
      regions,
      pageData,
      layoutData: layoutData,
    },
  };
}
